import type { AppointmentTypeDto } from '@shared/core/appointment-types'
import { Copy } from 'lucide-react'
import { useEffect, useRef, useState, type KeyboardEvent } from 'react'
import type { CopyFromMenuProps } from './copy-from-menu.interface'
import classes from './copy-from-menu.module.scss'

const COPY_FROM_LABEL = 'Copy from…'
const NO_SOURCES_TITLE = 'You have no other appointment types to copy from.'

export function CopyFromMenu({ sources, onCopy }: CopyFromMenuProps) {
  const [isOpen, setIsOpen] = useState(false)
  const rootRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!isOpen) return undefined
    const closeOnOutsideClick = (event: MouseEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) setIsOpen(false)
    }
    document.addEventListener('mousedown', closeOnOutsideClick)
    return () => document.removeEventListener('mousedown', closeOnOutsideClick)
  }, [isOpen])

  const closeOnEscape = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Escape') setIsOpen(false)
  }

  const pick = (source: AppointmentTypeDto) => {
    setIsOpen(false)
    onCopy(source.booking_questions ?? [])
  }

  return (
    <div ref={rootRef} className={classes.root} onKeyDown={closeOnEscape}>
      <button
        type="button"
        className={classes.trigger}
        aria-haspopup="menu"
        aria-expanded={isOpen}
        disabled={sources.length === 0}
        title={sources.length === 0 ? NO_SOURCES_TITLE : undefined}
        onClick={() => setIsOpen((open) => !open)}
      >
        <Copy size={16} strokeWidth={2.2} aria-hidden="true" />
        {COPY_FROM_LABEL}
      </button>
      {isOpen && (
        <ul role="menu" aria-label="Copy questions from" className={classes.menu}>
          {sources.map((source) => (
            <li key={source.id} role="none">
              <button type="button" role="menuitem" className={classes.item} onClick={() => pick(source)}>
                <span className={classes.name}>{source.name}</span>
                <span className={classes.count}>{questionCountLabel(source)}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

function questionCountLabel(source: AppointmentTypeDto): string {
  const count = source.booking_questions?.length ?? 0
  return count === 1 ? '1 question' : `${count} questions`
}
